// Register.tsx - CON NUEVO DISEÑO PERO MISMAS FUNCIONALIDADES
import { useEffect, useState } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { useNavigate, useLocation } from 'react-router-dom';
import { GoogleLogin, CredentialResponse } from '@react-oauth/google';
import { Eye, EyeOff, Smile, Info, UserCheck, ClipboardList, CalendarDays, Building2, User, Search, Stethoscope, Sparkles, Link2 } from 'lucide-react';
import TermsPrivacyModal from './TermsPrivacyModal';
import { scheduleTokenRefresh } from '../services/api';
import { SOPORTE_WHATSAPP_URL } from './WhatsAppButton';
import { RECIEN_REGISTRADO_KEY } from './OnboardingChecker';

// Configuración de la API
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Interfaces
interface RegisterFormData {
  nombre: string;
  apellido: string;
  email: string;
  telefono?: string;
  contrasenia: string;
  confirmarContrasenia: string;
  esProfesional: boolean;
  profesionalType?: 'salud' | 'belleza';
  especialidad?: string;
  terminos: boolean;
}

interface SuccessModalProps {
  isOpen: boolean;
  userType: 'cliente' | 'profesional';
  userName: string;
  professionalType?: 'salud' | 'belleza';
  specialty?: string;
  onContinue: () => void;
}

interface ExistingUserModalProps {
  isOpen: boolean;
  userEmail: string;
  userName: string;
  authMethod: string;
  onLogin: () => void;
  onClose: () => void;
}

interface GoogleAuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  message: string;
  showLoginOption?: boolean;
  onLogin?: () => void;
}

// Verificar si Google OAuth está disponible
const isGoogleAuthAvailable = () => {
  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
  const enabled = process.env.REACT_APP_ENABLE_GOOGLE_AUTH !== 'false';
  return !!(clientId && clientId !== '' && enabled);
};

// Modal para Google Auth
const GoogleAuthModal: React.FC<GoogleAuthModalProps> = ({ 
  isOpen, 
  onClose, 
  message, 
  showLoginOption = false,
  onLogin 
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full p-8 transform animate-in zoom-in-95 duration-200">
        <div className="flex justify-center mb-6">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
            <svg className="w-10 h-10 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        </div>
        
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-3 flex items-center justify-center gap-2">
            {showLoginOption ? <>Ya tenés cuenta <Smile className="w-6 h-6 text-yellow-500" /></> : <>Información importante <Info className="w-6 h-6 text-blue-500" /></>}
          </h2>
          <p className="text-gray-600">
            {message}
          </p>
        </div>
        
        <div className="space-y-4">
          {showLoginOption && onLogin && (
            <button
              onClick={onLogin}
              className="w-full bg-gradient-to-r from-blue-500 to-green-500 text-white py-4 px-6 rounded-xl font-semibold text-lg hover:shadow-lg hover:scale-105 transition-all duration-300"
            >
              Ir a Login
            </button>
          )}
          
          <button
            onClick={onClose}
            className={`w-full py-4 px-6 rounded-xl font-semibold text-lg hover:scale-105 transition-all duration-300 ${
              showLoginOption 
                ? 'bg-gray-100 text-gray-700 hover:bg-gray-200' 
                : 'bg-gradient-to-r from-blue-500 to-green-500 text-white hover:shadow-lg'
            }`}
          >
            {showLoginOption ? 'Cerrar' : 'Entendido'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Modal mejorado para usuarios existentes
const ExistingUserModal: React.FC<ExistingUserModalProps> = ({ 
  isOpen, 
  userEmail, 
  userName, 
  authMethod, 
  onLogin, 
  onClose 
}) => {
  if (!isOpen) return null;

  const getAuthMethodText = () => {
    switch (authMethod) {
      case 'google': return 'con Google';
      case 'manual': return 'con email y contraseña';
      default: return '';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full p-8 transform animate-in zoom-in-95 duration-200">
        {/* Icono de advertencia/info */}
        <div className="flex justify-center mb-6">
          <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center">
            <svg className="w-10 h-10 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L5.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
        </div>

        {/* Mensaje */}
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-3 flex items-center justify-center gap-2"> ¡Ya tenés una cuenta! <UserCheck className="w-7 h-7 text-green-500" /> </h2>
          <div className="space-y-2">
            <p className="text-gray-600">
              Hola <span className="font-semibold text-gray-800">{userName}</span>,
            </p>
            <p className="text-gray-600">
              el email <span className="font-semibold text-blue-600">{userEmail}</span> ya está registrado en consultAR.
            </p>
            <div className="bg-blue-50 p-3 rounded-lg mt-4">
              <p className="text-sm text-blue-700">
                <span className="font-medium">Método de registro:</span> {getAuthMethodText()}
              </p>
            </div>
          </div>
        </div>

        {/* Opciones */}
        <div className="space-y-4">
          <button
            onClick={onLogin}
            className="w-full bg-gradient-to-r from-blue-500 to-green-500 text-white py-4 px-6 rounded-xl font-semibold text-lg hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center justify-center space-x-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013 3v1" />
            </svg>
            <span>Iniciar Sesión</span>
          </button>
          <button
            onClick={onClose}
            className="w-full bg-gray-100 text-gray-700 py-4 px-6 rounded-xl font-semibold hover:bg-gray-200 transition-all duration-200"
          >
            Usar otro email
          </button>
        </div>
      </div>
    </div>
  );
};

// Componente Modal de Éxito ACTUALIZADO
const SuccessModal: React.FC<SuccessModalProps> = ({ 
  isOpen, 
  userType, 
  userName, 
  professionalType, 
  specialty, 
  onContinue 
}) => {
  if (!isOpen) return null;

  // Los pasos del profesional son los del asistente que se abre al entrar
  // (OnboardingWizard): antes prometía "completar tu perfil" y "configurar
  // tu especialidad", que ya la había elegido, y después le pedía otra cosa.
  const getNextSteps = () => {
    if (userType === 'profesional') {
      return [
        { icon: <ClipboardList className="w-5 h-5 text-sky-600" />, text: 'Tu primer servicio' },
        { icon: <CalendarDays className="w-5 h-5 text-sky-600" />, text: 'Tus días y horarios' },
        { icon: <Link2 className="w-5 h-5 text-sky-600" />, text: `Tu link para que ${professionalType === 'belleza' ? 'tus clientes' : 'tus pacientes'} reserven` }
      ];
    }
    return [
      { icon: <User className="w-5 h-5 text-green-500" />, text: 'Completar tu perfil' },
      { icon: <Search className="w-5 h-5 text-blue-500" />, text: 'Buscar profesionales cerca tuyo' },
      { icon: <CalendarDays className="w-5 h-5 text-indigo-500" />, text: 'Reservar tu primer turno' }
    ];
  };

  const getProfessionalTypeText = () => {
    if (userType !== 'profesional') return 'cliente';
    if (professionalType === 'belleza') return `profesional de belleza${specialty ? ` (${specialty})` : ''}`;
    return `profesional de la salud${specialty ? ` (${specialty})` : ''}`;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full p-8 transform animate-in zoom-in-95 duration-200">
        {/* Icono de éxito */}
        <div className="flex justify-center mb-6">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
            <svg className="w-10 h-10 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
        </div>

        {/* Mensaje de bienvenida */}
        <div className="text-center mb-7">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">¡Bienvenido/a, {userName}!</h2>
          <p className="text-gray-600">
            Tu cuenta de <span className="font-semibold text-sky-700">{getProfessionalTypeText()}</span> ya está creada.
            {userType === 'profesional' && ' Ahora dejamos tu página lista: te lleva dos minutos.'}
          </p>
        </div>

        {/* Próximos pasos */}
        <div className="mb-8 text-left">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500 mb-3">
            {userType === 'profesional' ? 'Lo que sigue' : 'Próximos pasos'}
          </h3>
          <div className="space-y-2.5">
            {getNextSteps().map((step, index) => (
              <div key={index} className="flex items-center gap-3 rounded-xl bg-sky-50/70 border border-sky-100 px-3 py-2.5">
                <span className="w-7 h-7 rounded-full bg-white border border-sky-200 flex items-center justify-center text-sm font-semibold text-sky-700 flex-shrink-0">
                  {index + 1}
                </span>
                {step.icon}
                <span className="text-gray-800 font-medium">{step.text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Botón continuar */}
        <button
          onClick={onContinue}
          className="w-full bg-gradient-to-r from-blue-500 to-green-500 text-white py-4 px-6 rounded-xl font-semibold text-lg hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center justify-center space-x-2"
        >
          <span>Empezar</span>
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
          </svg>
        </button>
      </div>
    </div>
  );
};

// Ayuda por WhatsApp dentro de cada paso: en el celular reemplaza a la burbuja
// de Soporte, que en esta pantalla tapaba los botones.
const AyudaRegistro: React.FC = () => (
  <p className="mt-5 text-center text-xs text-gray-500">
    ¿Problemas para registrarte?{' '}
    <a
      href={SOPORTE_WHATSAPP_URL}
      target="_blank"
      rel="noopener noreferrer"
      className="font-medium text-green-600 hover:underline"
    >
      Escribinos por WhatsApp
    </a>
  </p>
);

const Register = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // En el celular la burbuja de Soporte tapaba el "Continuar" y la contraseña:
  // acá se esconde (regla en index.css) y cada paso tiene su link de ayuda.
  useEffect(() => {
    document.body.classList.add('registro-visible');
    return () => document.body.classList.remove('registro-visible');
  }, []);

  // 🆕 REFERIDOS: capturar ?ref=CODIGO de la URL. Se persiste en sessionStorage
  // para que sobreviva navegación intermedia (ej: popup de Google, ir y volver).
  const refFromUrl = new URLSearchParams(location.search).get('ref');
  if (refFromUrl) {
    sessionStorage.setItem('consultar_ref_code', refFromUrl);
  }
  const referralCode = refFromUrl || sessionStorage.getItem('consultar_ref_code') || undefined;

  // Obtener datos pre-llenados del modal
  const prefilledData = (location.state as any)?.prefilledData;
  
  console.log('📋 Datos pre-llenados recibidos:', prefilledData);

  // Configurar formulario con valores por defecto
  const { register, handleSubmit, watch, formState: { errors } } = useForm<RegisterFormData>({
    defaultValues: prefilledData ? {
      nombre: prefilledData.nombre || '',
      apellido: prefilledData.apellido || '',
      email: prefilledData.email || '',
      telefono: prefilledData.telefono || '',
      esProfesional: false,
      terminos: true  // ✅ Tildado por defecto
    } : {
      terminos: true  // ✅ Tildado por defecto
    }
  });

  // Estados ACTUALIZADOS
  const [selectedRole, setSelectedRole] = useState<'cliente' | 'profesional' | null>(
    prefilledData?.userType === 'cliente' ? 'cliente' : null
  );
  const [selectedProfessionalType, setSelectedProfessionalType] = useState<'salud' | 'belleza' | null>(null);
  const [selectedSpecialty, setSelectedSpecialty] = useState<string>('');
  const [currentStep, setCurrentStep] = useState(
    prefilledData?.userType === 'cliente' ? 3 : 1
  );
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showExistingUserModal, setShowExistingUserModal] = useState(false);
  const [showGoogleModal, setShowGoogleModal] = useState(false);
  const [googleModalMessage, setGoogleModalMessage] = useState('');
  const [showLoginOption, setShowLoginOption] = useState(false);
  const [registerError, setRegisterError] = useState('');
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsModalTab, setTermsModalTab] = useState<'terms' | 'privacy'>('terms');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [registeredUser, setRegisteredUser] = useState<{
    name: string;
    type: 'cliente' | 'profesional';
    professionalType?: 'salud' | 'belleza';
    specialty?: string;
  } | null>(null);
  const [existingUser, setExistingUser] = useState<{
    email: string;
    nombre: string;
    auth_method: string;
  } | null>(null);

  // Watch para campos reactivos
  const watchEsProfesional = watch('esProfesional', false);
  const watchProfesionalType = watch('profesionalType');
  const watchContrasenia = watch('contrasenia');

  // Verificar disponibilidad de Google Auth
  const googleAuthEnabled = isGoogleAuthAvailable();

  console.log('🔧 Register - Google Auth Status:', {
    enabled: googleAuthEnabled,
    clientId: process.env.REACT_APP_GOOGLE_CLIENT_ID ? 'CONFIGURADO' : 'NO CONFIGURADO'
  });

  // Especialidades por tipo de profesional
  const especialidades = {
    salud: [
      'Medicina General', 
      'Medicina Familiar',
      'Medicina Interna',
      'Cardiología', 
      'Cirugía Cardiovascular',
      'Flebología',
      'Hematología',
      'Cirugía General',
      'Cirugía Plástica',
      'Traumatología',
      'Neurocirugía',
      'Urología',
      'Pediatría', 
      'Neonatología',
      'Ginecología', 
      'Obstetricia',
      'Dermatología',
      'Dermatología Estética',
      'Medicina Estética',
      'Neurología',
      'Neurología Infantil', 
      'Psiquiatría', 
      'Psicología', 
      'Neuropsicología',
      'Oftalmología', 
      'Otorrinolaringología',
      'Neumología',
      'Gastroenterología',
      'Endocrinología',
      'Nefrología',
      'Reumatología',
      'Odontología General', 
      'Ortodoncia',
      'Cirugía Bucal',
      'Endodoncia',
      'Periodoncia',
      'Odontopediatría',
      'Radiología',
      'Patología',
      'Medicina Nuclear',
      'Laboratorio Clínico',
      'Genética Médica',
      'Medicina Reproductiva',
      'Oncología',
      'Infectología',
      'Medicina del Deporte',
      'Medicina del Trabajo',
      'Geriatría',
      'Kinesiología', 
      'Fisioterapia',
      'Terapia Ocupacional',
      'Fonoaudiología',
      'Nutrición', 
      'Psicopedagogía',
      'Medicina Ayurveda',
      'Acupuntura',
      'Homeopatía',
      'Medicina China',
      'Naturopatía',
      'Medicina de Emergencia',
      'Cuidados Intensivos',
      'Anestesiología',
      'Medicina Legal',
      'Medicina Hiperbárica',
      'Toxicología',
      'Inmunología',
      'Medicina Transfusional',
      'Profesional'
    ],
    belleza: [
      'Lashista (Pestañas y Cejas)', 
      'Lifting de Pestañas',
      'Extensiones de Pestañas',
      'Microblading de Cejas',
      'Laminado de Cejas',
      'Manicura/Nail Art', 
      'Uñas Esculpidas',
      'Esmaltado Semipermanente',
      'Nail Art Avanzado',
      'Peluquería General', 
      'Colorista',
      'Barbería',
      'Extensiones de Cabello',
      'Alisados y Tratamientos',
      'Peinados para Eventos',
      'Estética Facial', 
      'Limpieza Facial',
      'Radiofrecuencia',
      'Microdermoabrasión',
      'Peeling Químico',
      'Hidrafacial',
      'Maquillaje Profesional',
      'Maquillaje de Novias',
      'Automaquillaje',
      'Maquillaje Artístico',
      'Depilación Láser',
      'Depilación con Cera',
      'Depilación IPL',
      'Masajes Relajantes', 
      'Masajes Descontracturantes',
      'Drenaje Linfático',
      'Masajes con Piedras Calientes',
      'Micropigmentación', 
      'Tatuajes Estéticos',
      'Micropigmentación de Labios',
      'Tatuaje de Cejas',
      'Tratamientos Reductores',
      'Ultracavitación',
      'Presoterapia',
      'Crioterapia Estética',
      'Imagen y Asesoría',
      'Organización de Eventos de Belleza',
      'Venta de Cosméticos',
      'Profesional'
    ]
  };

  // Función para manejar registro exitoso
  const handleSuccessfulRegister = (userData: any, token?: string) => {
    console.log('Registro exitoso:', userData);
    
    // ✅ Guardar TOKEN si existe (crítico para que funcione el wizard)
    if (token) {
      localStorage.setItem('token', token);
      scheduleTokenRefresh();
      console.log('✅ Token guardado en localStorage');
    }
    
    // Guardar datos del usuario
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('isLoggedIn', 'true');
    
    // Disparar evento para actualizar header
    window.dispatchEvent(new CustomEvent('userLogin', { detail: userData }));

    // El panel abre el asistente de primeros pasos apenas entra, sin esperar
    // a preguntarle a la API si ya tiene servicios (ver OnboardingChecker).
    if (userData.es_profesional && userData.id) {
      try { sessionStorage.setItem(RECIEN_REGISTRADO_KEY, String(userData.id)); } catch { /* noop */ }
    }

    // Mostrar modal de éxito. El servidor manda `professional_type`: con
    // `professionalType` (que no existe) una cuenta de belleza figuraba como
    // "profesional de la salud".
    setRegisteredUser({
      name: userData.nombre,
      type: userData.es_profesional ? 'profesional' : 'cliente',
      professionalType: userData.professional_type || userData.professionalType || undefined,
      specialty: userData.especialidad || undefined
    });
    setShowSuccessModal(true);
  };

  // Función para verificar si el email ya existe antes del registro manual
  const checkEmailExists = async (email: string) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/check-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error verificando email:', error);
      return { exists: false };
    }
  };

  // Registro manual
  const onSubmit: SubmitHandler<RegisterFormData> = async (data) => {
    setIsLoading(true);
    setRegisterError('');
    console.log('Iniciando registro manual:', { email: data.email, esProfesional: data.esProfesional });

    // Validación de contraseñas
    if (data.contrasenia !== data.confirmarContrasenia) {
      setRegisterError('Las contraseñas no coinciden');
      setIsLoading(false);
      return;
    }

    // Validación para profesionales
    if (selectedRole === 'profesional' && (!selectedProfessionalType || !selectedSpecialty)) {
      setRegisterError('Para profesionales se requiere tipo y especialidad');
      setIsLoading(false);
      return;
    }

    try {
      // Primero verificar si el email ya existe
      const emailCheck = await checkEmailExists(data.email);
      if (emailCheck.exists) {
        console.log('Email ya existe:', emailCheck.user);
        setExistingUser({
          email: emailCheck.user.email,
          nombre: emailCheck.user.nombre + ' ' + emailCheck.user.apellido,
          auth_method: emailCheck.user.auth_method || 'manual'
        });
        setShowExistingUserModal(true);
        setIsLoading(false);
        return;
      }

      const response = await fetch(`${API_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nombre: data.nombre,
          apellido: data.apellido,
          email: data.email,
          telefono: data.telefono,
          contrasenia: data.contrasenia,
          esProfesional: selectedRole === 'profesional',
          profesionalType: selectedRole === 'profesional' ? selectedProfessionalType : undefined,
          especialidad: selectedRole === 'profesional' ? selectedSpecialty : undefined,
          referralCode
        })
      });

      const result = await response.json();

      if (response.ok && result.success) {
        handleSuccessfulRegister(result.user, result.token);
      } else if (response.status === 409) {
        // Email ya registrado - mostrar modal
        setExistingUser({
          email: result.existingUser?.email || data.email,
          nombre: (result.existingUser?.nombre + ' ' + result.existingUser?.apellido) || 'Usuario',
          auth_method: result.existingUser?.auth_method || 'manual'
        });
        setShowExistingUserModal(true);
      } else {
        setRegisterError(result.error || 'Error en el registro');
      }
    } catch (error) {
      console.error('Error en registro:', error);
      setRegisterError('No pudimos conectarnos. Probá de nuevo en unos segundos.');
    } finally {
      setIsLoading(false);
    }
  };

  // Registro con Google
  const handleGoogleSuccess = async (response: CredentialResponse) => {
    if (!googleAuthEnabled) {
      setRegisterError('Google OAuth no está configurado correctamente');
      return;
    }

    console.log('Google register iniciado:', response);
    setIsLoading(true);
    setRegisterError('');

    try {
      const res = await fetch(`${API_URL}/api/auth/google`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          token: response.credential,
          isLogin: false, // Esto es registro
          esProfesional: selectedRole === 'profesional',
          profesionalType: selectedProfessionalType,
          especialidad: selectedSpecialty,
          referralCode
        })
      });
      
      const data = await res.json();
      console.log('Respuesta del servidor Google Register:', data);

      if (res.ok && data.success) {
        handleSuccessfulRegister(data.user, data.token);
      } else if (res.status === 409) {
        // Usuario ya existe - sugerir login
        setExistingUser({
          email: data.existingUser?.email || data.email,
          nombre: (data.existingUser?.nombre + ' ' + data.existingUser?.apellido) || 'Usuario Google',
          auth_method: data.existingUser?.auth_method || 'google'
        });
        setShowExistingUserModal(true);
      } else if (res.status === 400) {
        // Error de validación para profesionales
        setRegisterError(data.error || 'Error en el registro con Google');
      } else {
        setRegisterError(data.error || 'Error en el registro con Google');
      }
    } catch (error) {
      console.error('Error en Google register:', error);
      setRegisterError('Error de conexión con Google. Por favor intenta nuevamente.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleError = () => {
    console.log('Google register cancelado o falló');
    setRegisterError('Registro con Google cancelado o falló');
  };

  // Función para ir al login desde el modal
  const handleGoToLogin = () => {
    setShowGoogleModal(false);
    navigate('/login');
  };

  const handleSuccessModalContinue = () => {
    setShowSuccessModal(false);
    // Redirigir según el tipo de usuario
    if (registeredUser?.type === 'profesional') {
      navigate('/dashboard/profesional');
    } else {
      navigate('/dashboard/paciente');
    }
  };

  const handleExistingUserLogin = () => {
    setShowExistingUserModal(false);
    navigate('/login');
  };

  const handleExistingUserClose = () => {
    setShowExistingUserModal(false);
    setExistingUser(null);
    // Resetear al paso 1 para que pueda intentar con otro email
    setCurrentStep(1);
    setSelectedRole(null);
    setSelectedProfessionalType(null);
    setSelectedSpecialty('');
  };

  // FUNCIÓN CORREGIDA: nextStep
  const nextStep = () => {
    if (selectedRole) {
      if (selectedRole === 'cliente') {
        setCurrentStep(3); // Los clientes van directo al paso 3
      } else {
        setCurrentStep(2); // Los profesionales van al paso 2
      }
    }
  };

  const nextStepFromProfessional = () => {
    if (selectedRole === 'cliente' || (selectedRole === 'profesional' && selectedProfessionalType && selectedSpecialty)) {
      setCurrentStep(3);
    }
  };

  // FUNCIÓN CORREGIDA: prevStep
  const prevStep = () => {
    if (currentStep === 3) {
      // Si estamos en el paso 3, verificamos de dónde venimos
      if (selectedRole === 'cliente') {
        setCurrentStep(1); // Los clientes regresan al paso 1
      } else {
        setCurrentStep(2); // Los profesionales regresan al paso 2
      }
    } else if (currentStep === 2) {
      setCurrentStep(1);
      setSelectedProfessionalType(null);
      setSelectedSpecialty('');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      {/* Elementos decorativos de fondo. Quietos: difuminados y animados
          obligaban a redibujarlos en cada cuadro, en la primera pantalla que
          ve alguien que llega desde un anuncio. */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-10 w-20 h-20 bg-blue-500/10 rounded-full blur-xl"></div>
        <div className="absolute top-1/3 right-20 w-32 h-32 bg-green-500/10 rounded-full blur-xl"></div>
      </div>

      <div className="relative max-w-md w-full space-y-8">
        {/* PASO 1: Selección de rol */}
        {currentStep === 1 && (
          <div className="bg-white p-8 rounded-2xl shadow-2xl border border-gray-100">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="flex justify-center mb-4">
                <div className="bg-gradient-to-r from-blue-500 to-green-500 p-3 rounded-2xl">
                  <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                  </svg>
                </div>
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-2">¡Bienvenido a consultAR!</h2>
              <p className="text-gray-600">¿Cómo vas a usar consultAR?</p>
            </div>

            {/* Selección de rol */}
            <div className="space-y-4 mb-8">
              <div className={`cursor-pointer p-6 rounded-xl border-2 transition-all duration-300 ${
                selectedRole === 'cliente' ? 'border-blue-500 bg-blue-50 shadow-lg scale-105' : 'border-gray-200 hover:border-blue-500 hover:shadow-md hover:scale-102'
              }`}
              onClick={() => setSelectedRole('cliente')}
              >
                <div className="flex items-center space-x-4">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                    selectedRole === 'cliente' ? 'bg-blue-500' : 'bg-blue-100'
                  }`}>
                    <span className={`block ${selectedRole === 'cliente' ? 'text-white' : 'text-blue-600'}`}>
                      {prefilledData?.userType === 'cliente' ? <Building2 className="w-6 h-6" /> : <User className="w-6 h-6" />}
                    </span>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900">
                      {prefilledData?.userType === 'cliente' ? 'Soy Paciente' : 'Soy Cliente'}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {prefilledData?.userType === 'cliente' 
                        ? 'Quiero gestionar mis turnos médicos' 
                        : 'Quiero reservar turnos y servicios'}
                    </p>
                  </div>
                  {selectedRole === 'cliente' && (
                    <div className="text-blue-500">
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  )}
                </div>
              </div>

              <div className={`p-6 rounded-xl border-2 transition-all duration-300 ${
                prefilledData?.userType === 'cliente' 
                  ? 'border-gray-200 bg-gray-100 opacity-50 cursor-not-allowed' // Deshabilitado
                  : selectedRole === 'profesional' 
                    ? 'border-green-500 bg-green-50 shadow-lg scale-105' 
                    : 'border-gray-200 hover:border-green-500 hover:shadow-md hover:scale-102 cursor-pointer'
              }`}
              onClick={() => !prefilledData?.userType && setSelectedRole('profesional')}
              >
                <div className="flex items-center space-x-4">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                    selectedRole === 'profesional' ? 'bg-green-500' : 'bg-green-100'
                  }`}>
                    <span className={`block ${selectedRole === 'profesional' ? 'text-white' : 'text-green-600'}`}>
                      <Stethoscope className="w-6 h-6" />
                    </span>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900">Soy Profesional</h3>
                    <p className="text-sm text-gray-600">Quiero gestionar mi agenda y servicios</p>
                    {prefilledData?.userType === 'cliente' && (
                      <p className="text-xs text-gray-500 mt-1">Esta opción no está disponible para tu registro</p>
                    )}
                  </div>
                  {selectedRole === 'profesional' && (
                    <div className="text-green-500">
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Botón continuar */}
            <button
              onClick={nextStep}
              disabled={!selectedRole}
              className="w-full bg-gradient-to-r from-blue-500 to-green-500 text-white py-4 px-6 rounded-xl font-semibold text-lg hover:shadow-lg hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center space-x-2"
            >
              <span>Continuar</span>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </button>

            {/* Login alternativo */}
            <div className="text-center mt-6">
              <p className="text-sm text-gray-600">
                ¿Ya tenés cuenta?
                <button
                  onClick={() => navigate('/login')}
                  className="text-blue-500 hover:underline font-medium ml-1"
                >
                  Iniciá sesión
                </button>
              </p>
            </div>
            <AyudaRegistro />
          </div>
        )}

        {/* PASO 2: Selección de tipo profesional y especialidad */}
        {currentStep === 2 && selectedRole === 'profesional' && (
          <div className="bg-white p-8 rounded-2xl shadow-2xl border border-gray-100">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="flex justify-center mb-4">
                <div className="bg-gradient-to-r from-blue-500 to-green-500 p-3 rounded-2xl">
                  <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                  </svg>
                </div>
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-2">Tu perfil profesional</h2>
              <p className="text-gray-600">Elegí tu rubro y tu especialidad</p>
            </div>

            {/* Rubro. Los íconos estaban vacíos (un <span> sin nada adentro
                que dejaba un hueco arriba de cada palabra). */}
            <div className="mb-8">
              <label className="block text-sm font-medium text-gray-700 mb-3">Rubro</label>
              <div className="grid grid-cols-2 gap-4">
                <div
                  className={`cursor-pointer p-4 rounded-xl border-2 transition-all duration-300 ${
                    selectedProfessionalType === 'salud'
                      ? 'border-blue-500 bg-blue-50 shadow-lg scale-105'
                      : 'border-gray-200 hover:border-blue-500 hover:shadow-md hover:scale-102'
                  }`}
                  onClick={() => setSelectedProfessionalType('salud')}
                >
                  <div className="flex flex-col items-center gap-2">
                    <Stethoscope className="w-7 h-7 text-blue-600" />
                    <span className="font-medium text-gray-900">Salud</span>
                  </div>
                </div>
                <div
                  className={`cursor-pointer p-4 rounded-xl border-2 transition-all duration-300 ${
                    selectedProfessionalType === 'belleza'
                      ? 'border-pink-500 bg-pink-50 shadow-lg scale-105'
                      : 'border-gray-200 hover:border-pink-500 hover:shadow-md hover:scale-102'
                  }`}
                  onClick={() => setSelectedProfessionalType('belleza')}
                >
                  <div className="flex flex-col items-center gap-2">
                    <Sparkles className="w-7 h-7 text-pink-500" />
                    <span className="font-medium text-gray-900">Belleza</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Especialidad */}
            {selectedProfessionalType && (
              <div className="mb-8">
                <label className="block text-sm font-medium text-gray-700 mb-3">Especialidad</label>
                <select
                  value={selectedSpecialty}
                  onChange={(e) => setSelectedSpecialty(e.target.value)}
                  className="w-full p-4 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-blue-500 transition-all duration-300"
                >
                  <option value="">Elegí tu especialidad</option>
                  {especialidades[selectedProfessionalType].map((especialidad) => (
                    <option key={especialidad} value={especialidad}>
                      {especialidad}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Botones de navegación */}
            <div className="space-y-4">
              <button
                onClick={nextStepFromProfessional}
                disabled={!selectedProfessionalType || !selectedSpecialty}
                className="w-full bg-gradient-to-r from-blue-500 to-green-500 text-white py-4 px-6 rounded-xl font-semibold text-lg hover:shadow-lg hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center space-x-2"
              >
                <span>Continuar</span>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </button>
              <button
                onClick={prevStep}
                className="w-full bg-gray-100 text-gray-700 py-4 px-6 rounded-xl font-semibold hover:bg-gray-200 transition-all duration-200 flex items-center justify-center space-x-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 17l-5-5m0 0l5-5m-5 5h12" />
                </svg>
                <span>Volver</span>
              </button>
            </div>
            <AyudaRegistro />
          </div>
        )}

        {/* PASO 3: Formulario de registro */}
        {currentStep === 3 && (
          <div className="bg-white p-8 rounded-2xl shadow-2xl border border-gray-100">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="flex justify-center mb-4">
                <div className="bg-gradient-to-r from-blue-500 to-green-500 p-3 rounded-2xl">
                  <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                  </svg>
                </div>
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-2">Tus datos</h2>
              <p className="text-gray-600">
                {selectedRole === 'profesional' 
                  ? `Profesional de ${selectedProfessionalType === 'salud' ? 'salud' : 'belleza'} - ${selectedSpecialty}`
                  : selectedRole === 'cliente' 
                    ? (prefilledData?.userType === 'cliente' ? 'Paciente' : 'Cliente')
                    : 'Usuario'
                }
              </p>
            </div>

            {/* Formulario. text-left: la app centra todo (.App de CRA) y las
                etiquetas quedaban en el medio, lejos de cada campo. */}
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 text-left">
              {/* Nombre y Apellido */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nombre</label>
                  <input
                    type="text"
                    {...register('nombre', { required: 'El nombre es obligatorio' })}
                    className="w-full p-4 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-blue-500 transition-all duration-300"
                    placeholder="Tu nombre"
                  />
                  {errors.nombre && <p className="text-red-500 text-sm mt-1">{errors.nombre.message}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Apellido</label>
                  <input
                    type="text"
                    {...register('apellido', { required: 'El apellido es obligatorio' })}
                    className="w-full p-4 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-blue-500 transition-all duration-300"
                    placeholder="Tu apellido"
                  />
                  {errors.apellido && <p className="text-red-500 text-sm mt-1">{errors.apellido.message}</p>}
                </div>
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                <input
                  type="email"
                  {...register('email', { 
                    required: 'El email es obligatorio',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Email inválido'
                    }
                  })}
                  className="w-full p-4 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-blue-500 transition-all duration-300"
                  placeholder="tu@email.com"
                />
                {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>}
              </div>

              {/* Teléfono */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Teléfono (Opcional)</label>
                <input
                  type="tel"
                  {...register('telefono')}
                  className="w-full p-4 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-blue-500 transition-all duration-300"
                  placeholder="+54 11 1234-5678"
                />
              </div>

              {/* Contraseñas */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Contraseña</label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      {...register('contrasenia', {
                        required: 'La contraseña es obligatoria',
                        minLength: {
                          value: 6,
                          message: 'La contraseña debe tener al menos 6 caracteres'
                        }
                      })}
                      className="w-full p-4 pr-12 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-blue-500 transition-all duration-300"
                      placeholder="••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none transition-colors"
                      aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  {errors.contrasenia && <p className="text-red-500 text-sm mt-1">{errors.contrasenia.message}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Repetí la contraseña</label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      {...register('confirmarContrasenia', {
                        required: 'Repetí tu contraseña',
                        validate: value => value === watchContrasenia || 'Las contraseñas no coinciden'
                      })}
                      className="w-full p-4 pr-12 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-blue-500 transition-all duration-300"
                      placeholder="••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none transition-colors"
                      aria-label={showConfirmPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                    >
                      {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  {errors.confirmarContrasenia && <p className="text-red-500 text-sm mt-1">{errors.confirmarContrasenia.message}</p>}
                </div>
              </div>

              {/* Términos y condiciones */}
              <div className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  {...register('terminos', { required: 'Tenés que aceptar los términos y condiciones' })}
                  className="mt-1 w-5 h-5 text-blue-500 border-2 border-gray-300 rounded focus:ring-blue-500"
                />
                <label className="text-sm text-gray-700">
                  Acepto los{' '}
                  <button type="button" onClick={() => { setTermsModalTab('terms'); setShowTermsModal(true); }} className="text-blue-500 hover:underline font-medium">
                    términos y condiciones
                  </button>{' '}
                  y la{' '}
                  <button type="button" onClick={() => { setTermsModalTab('privacy'); setShowTermsModal(true); }} className="text-blue-500 hover:underline font-medium">
                    política de privacidad
                  </button>
                </label>
              </div>
              {errors.terminos && <p className="text-red-500 text-sm mt-1">{errors.terminos.message}</p>}

              <TermsPrivacyModal
                isOpen={showTermsModal}
                onClose={() => setShowTermsModal(false)}
                initialTab={termsModalTab}
              />

              {/* Botón de registro manual */}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-gradient-to-r from-blue-500 to-green-500 text-white py-4 px-6 rounded-xl font-semibold text-lg hover:shadow-lg hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center space-x-2"
              >
                {isLoading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Registrando...</span>
                  </>
                ) : (
                  <>
                    <span>Crear Cuenta</span>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </>
                )}
              </button>

              {/* Separador */}
              {googleAuthEnabled && (
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-white text-gray-500">O registrate con</span>
                  </div>
                </div>
              )}

              {/* Botón de Google */}
              {googleAuthEnabled && (
                <GoogleLogin
  onSuccess={handleGoogleSuccess}
  onError={handleGoogleError}
  useOneTap={false}
  text="signup_with"
  shape="rectangular"
  size="large"
  theme="outline"
  logo_alignment="left"
/>
              )}

              {/* Error general */}
              {registerError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
                  {registerError}
                </div>
              )}

              {/* Botón volver */}
              <button
                type="button"
                onClick={prevStep}
                className="w-full bg-gray-100 text-gray-700 py-4 px-6 rounded-xl font-semibold hover:bg-gray-200 transition-all duration-200 flex items-center justify-center space-x-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 17l-5-5m0 0l5-5m-5 5h12" />
                </svg>
                <span>Volver</span>
              </button>
            </form>
            <AyudaRegistro />
          </div>
        )}

        {/* Modal de éxito */}
        {registeredUser && (
          <SuccessModal
            isOpen={showSuccessModal}
            userType={registeredUser.type}
            userName={registeredUser.name}
            professionalType={registeredUser.professionalType}
            specialty={registeredUser.specialty}
            onContinue={handleSuccessModalContinue}
          />
        )}

        {/* Modal de usuario existente */}
        {existingUser && (
          <ExistingUserModal
            isOpen={showExistingUserModal}
            userEmail={existingUser.email}
            userName={existingUser.nombre}
            authMethod={existingUser.auth_method}
            onLogin={handleExistingUserLogin}
            onClose={handleExistingUserClose}
          />
        )}

        {/* Modal para Google Auth */}
        <GoogleAuthModal
          isOpen={showGoogleModal}
          onClose={() => setShowGoogleModal(false)}
          message={googleModalMessage}
          showLoginOption={showLoginOption}
          onLogin={handleGoToLogin}
        />
      </div>
    </div>
  );
};

export default Register;